import { apiClient, getErrorMessage } from './client'

// Re-export for convenience
export { getErrorMessage }

export interface Application {
  _id: string
  appId: string
  name: string
  description?: string
  url?: string
  passwordResetUrl?: string
  allowedRoles: string[]
  defaultNewUserRole?: string
  userQueryRoles: string[]
  repositoryRoles: {
    clients: string[]
    titles: string[]
  }
  webauthnOrigins: string[]
  settings?: {
    passkeysEnabled: boolean
    passkeyEnrollmentEnabled: boolean
  }
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface ApplicationsListResponse {
  applications: Application[]
  total: number
  page: number
  pages: number
}

export interface CreateApplicationData {
  appId: string
  name: string
  description?: string
  url?: string
  passwordResetUrl?: string
  allowedRoles?: string[]
  defaultNewUserRole?: string
  userQueryRoles?: string[]
  repositoryRoles?: { clients?: string[]; titles?: string[] }
}

export interface UpdateApplicationData {
  name?: string
  description?: string
  url?: string
  passwordResetUrl?: string
  isActive?: boolean
  allowedRoles?: string[]
  defaultNewUserRole?: string
  userQueryRoles?: string[]
  repositoryRoles?: { clients?: string[]; titles?: string[] }
  webauthnOrigins?: string[]
  settings?: { passkeysEnabled?: boolean; passkeyEnrollmentEnabled?: boolean }
}

export async function listApplications(
  includeInactive = false
): Promise<ApplicationsListResponse> {
  const params: Record<string, string> = { limit: '100' }
  if (!includeInactive) {
    params.isActive = 'true'
  }
  // When includeInactive is true, don't send isActive param - backend returns all
  const response = await apiClient.get<ApplicationsListResponse>('/admin/applications', {
    params,
  })
  return response.data
}

export async function getApplication(appId: string): Promise<Application> {
  const response = await apiClient.get<{ application: Application }>(
    `/admin/applications/${appId}`
  )
  return response.data.application
}

export async function createApplication(
  data: CreateApplicationData
): Promise<Application> {
  const response = await apiClient.post<{ application: Application }>(
    '/admin/applications',
    data
  )
  return response.data.application
}

export async function updateApplication(
  appId: string,
  data: UpdateApplicationData
): Promise<Application> {
  const response = await apiClient.patch<{ application: Application }>(
    `/admin/applications/${appId}`,
    data
  )
  return response.data.application
}

export async function deleteApplication(appId: string): Promise<void> {
  await apiClient.delete(`/admin/applications/${appId}`)
}
